import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  Fab,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Theme, createTheme } from '@mui/material/styles';
import { Image } from 'components-new';
import { ForwardedRef, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchData, sendToGA } from '@/components/util';
import { LeadCategory } from '@/_models/TourLead';
import { Tour } from '@/_models/Tour';
import { FAIcon } from '@packages/ui-components';

type Props = {
  tour: Tour;
  form: {
    imageSrc: string;
    phone?: boolean;
    name?: boolean;
    email?: boolean;
    checkbox1?: string;
    url?: string;
    time?: number;
    checkbox2?: string;
  };
  whitelabel: any;
  to: string;
  translation: any;
  rtl: boolean;
};

const Input = forwardRef(function Input(
  {
    label,
    type,
  }: {
    label: string;
    type?: string;
  },
  ref: ForwardedRef<HTMLInputElement>,
) {
  return (
    <TextField
      inputRef={ref}
      sx={{
        '& .MuiInputBase-root': {
          height: '50px',
        },
      }}
      label={label}
      type={type}
      fullWidth
      required
    />
  );
});

const LegalCheckbox = forwardRef(
  ({ label }: { label: string }, ref: ForwardedRef<HTMLInputElement>) => {
    return (
      <FormControlLabel
        componentsProps={{
          typography: {
            fontSize: {
              xs: '14px',
              md: '16px',
            },
          },
        }}
        sx={{ m: 0 }}
        // @ts-ignore
        control={<Checkbox ref={ref} required />}
        label={label}
      />
    );
  },
);

export default function AdRedesigned({ tour, form, to, translation, whitelabel, rtl }: Props) {
  const { id, title, userId, clientId } = tour;
  const { phone, name, email, checkbox1, url, time, checkbox2 } = form;

  const primaryColor = whitelabel?.primaryColor || '#2055ff';
  const secondaryColor = whitelabel?.secondaryColor || '#000000';

  const hasOnlyImage = useMemo(
    () => !name && !email && !phone && !checkbox1 && !checkbox2,
    [email, name, phone, checkbox1, checkbox2],
  );

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onClose = () => {
    setOpen(false);
  };

  const validatePhone = (value) => {
    const regex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
    return regex.test(value);
  };

  const validateEmail = (emailStr: string) => {
    const re =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(emailStr).toLowerCase());
  };

  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const legalRef = useRef<HTMLInputElement>(null);
  const legalRef2 = useRef<HTMLInputElement>(null);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nameValue = nameRef.current?.value;
    const emailValue = emailRef.current?.value;
    const phoneValue = phoneRef.current?.value;
    const checkbox1Value = legalRef.current?.checked;
    const checkbox2Value = legalRef2.current?.checked;

    if (email && !validateEmail(emailValue)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (phone && !validatePhone(phoneValue)) {
      toast.error('Please enter a valid phone number');
      return;
    }

    try {
      setSubmitted(true);

      sendToGA('event', {
        eventCategory: 'Lead',
        eventAction: 'submit',
        eventLabel: time ? 'image' : 'sweep',
      });

      const params = {
        link: window.location.href,
        tour: title,
        userId,
        clientId,
        name: nameValue,
        email: emailValue,
        phone: phoneValue,
        legalBookingBox: checkbox1Value,
        legalBookingBox2: checkbox2Value,
      };

      await fetchData('public/sendEmail', 'POST', {
        to,
        params,
      });
      toast.success('E-Mail sent');
      await fetchData('public/setTourLead', 'POST', {
        name: params.name,
        email: params.email,
        phone: params.phone,
        category: LeadCategory.InTourLeadImage,
        tourId: id,
      });
    } catch (error) {
      toast.error(error.message);
    } finally {
      onClose();
    }
  };

  useEffect(() => {
    // If time is present, then it's a timed lead form, if not, then it's a 3Dscan lead form
    if (time) {
      setTimeout(() => {
        setOpen(true);
        sendToGA('event', {
          eventCategory: 'Lead',
          eventAction: 'open',
          eventLabel: 'image',
        });
      }, time * 1000);
    } else {
      setOpen(true);
      sendToGA('event', {
        eventCategory: 'Lead',
        eventAction: 'open',
        eventLabel: 'sweep',
      });
    }
  }, [time]);

  const topButtonsMargin = '16px';

  return (
    <ThemeProvider
      theme={() =>
        createTheme({
          // @ts-ignore
          palette: {
            primary: {
              main: primaryColor,
            },
            secondary: {
              main: secondaryColor,
            },
          },
        })
      }
    >
      <Dialog
        dir={rtl ? 'rtl' : 'ltr'}
        open={open}
        maxWidth="md"
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            borderRadius: '25px',
            overflow: 'hidden',
          },
        }}
      >
        <Fab
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: topButtonsMargin,
            [rtl ? 'left' : 'right']: topButtonsMargin,
            width: '32px',
            height: '32px',
            minHeight: '32px',
            zIndex: 1,
            boxShadow: 'none',
            fontSize: '18px',
            background: '#fff',
          }}
        >
          <FAIcon icon="faTimes" />
        </Fab>
        {url && (
          <Chip
            component={Link}
            target="_blank"
            href={url}
            sx={{
              position: 'absolute',
              top: topButtonsMargin,
              [rtl ? 'right' : 'left']: topButtonsMargin,
              zIndex: 1,
              boxShadow: 'none',
              color: '#fff',
              fontWeight: 'bolder',
              cursor: 'pointer',
              background: 'rgba(0,0,0,0.4)',

              '&:hover': {
                textDecoration: 'none',
                color: '#fff',
              },
            }}
            label={
              <Stack direction="row" alignItems="center">
                <Box>
                  <FAIcon icon="faArrowUpRightFromSquare" />
                </Box>
                <Typography variant="body2" sx={{ color: '#fff', mx: 1 }}>
                  {translation?.clickToOpen || 'Click to Open'}
                </Typography>
              </Stack>
            }
          />
        )}
        <Card
          sx={{
            background: '#fff',
            width: '100%',
            padding: '0',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Image src={form.imageSrc} ratio={hasOnlyImage ? '4/3' : '21/9'} objectFit="cover" />
          {!hasOnlyImage && (
            <Box
              sx={{
                overflowY: 'auto',
                padding: {
                  xs: 2,
                },
              }}
            >
              <form onSubmit={handleSubmit}>
                <CardContent sx={{ padding: 0 }}>
                  <Stack spacing={2}>
                    {name && <Input ref={nameRef} label={translation?.name || 'Name'} />}
                    {email && (
                      <Input ref={emailRef} label={translation?.email || 'E-Mail'} type="email" />
                    )}
                    {phone && <Input ref={phoneRef} label={translation?.phone || 'Phone'} />}
                    <Box
                      sx={{
                        marginX: '-8px !important',
                        width: '100%',
                        textAlign: rtl ? 'right' : 'left',
                      }}
                    >
                      {checkbox1 && <LegalCheckbox ref={legalRef} label={checkbox1} />}
                      {checkbox2 && <LegalCheckbox ref={legalRef2} label={checkbox2} />}
                    </Box>
                  </Stack>
                </CardContent>
                <CardActions sx={{ padding: 0, mt: 2 }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={submitted}
                    sx={{
                      height: isDesktop ? '56px' : '50px',
                    }}
                  >
                    {translation?.submit || 'Submit'}
                  </Button>
                </CardActions>
              </form>
            </Box>
          )}
        </Card>
      </Dialog>
    </ThemeProvider>
  );
}
